import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import SiteLayout from "@/components/site/SiteLayout";
import { coverFor, formatDate } from "@/components/site/ArticleCard";
import ArticleCard from "@/components/site/ArticleCard";
import CommentSection from "@/components/site/CommentSection";
import ShareBar from "@/components/site/ShareBar";
import { Eye, Calendar, User } from "lucide-react";

const CAT_NAMES = {
  politics: "రాజకీయం",
  district: "జిల్లా వార్తలు",
  agriculture: "వ్యవసాయం",
  crime: "క్రైమ్",
  cinema: "సినిమా/వినోదం",
  special: "ప్రత్యేక కథనాలు",
};

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null); setNotFound(false);
    api.get(`/articles/${slug}`)
      .then((r) => {
        setArticle(r.data);
        return api.get("/articles", { params: { category: r.data.category, limit: 4 } });
      })
      .then((r) => setRelated((r?.data || []).filter((x) => x.slug !== slug).slice(0, 3)))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <SiteLayout>
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <h1 className="font-heading-te text-3xl mb-2">కథనం కనిపించలేదు</h1>
          <Link to="/" className="text-[#2E7D32] underline">తిరిగి హోమ్‌కు వెళ్ళండి</Link>
        </div>
      </SiteLayout>
    );
  }
  if (!article) {
    return <SiteLayout><div className="max-w-3xl mx-auto px-4 py-16 font-body-te text-[#4A4A4A]">లోడ్ అవుతోంది...</div></SiteLayout>;
  }

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <SiteLayout>
      <article className="max-w-4xl mx-auto px-4 py-10">
        <div className="mb-4">
          <Link
            to={`/category/${article.category}`}
            className="inline-block nv-badge"
            data-testid="article-category-link"
          >
            {CAT_NAMES[article.category]}
          </Link>
        </div>
        <h1 className="font-heading-te text-3xl sm:text-4xl md:text-5xl font-black leading-tight mb-4" data-testid="article-title">
          {article.title}
        </h1>
        {article.summary && (
          <p className="font-body-te text-lg text-[#4A4A4A] leading-relaxed mb-6 border-l-4 border-[#2E7D32] pl-4" data-testid="article-summary">
            {article.summary}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-4 text-xs text-[#4A4A4A] font-body-te uppercase tracking-wider border-y border-[#E5E4DC] py-3 mb-8">
          <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {article.author}</span>
          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {formatDate(article.created_at)}</span>
          <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {article.views || 0} views</span>
        </div>

        <div className="aspect-[16/9] mb-8 overflow-hidden bg-gray-100">
          <img src={coverFor(article)} alt={article.title} className="w-full h-full object-cover" draggable={false} />
        </div>

        <div className="nv-prose" data-testid="article-body">
          {article.body.split(/\n\n+/).map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-[#E5E4DC]">
          <ShareBar url={shareUrl} title={article.title} slug={article.slug} />
        </div>

        <CommentSection articleId={article.id} />
      </article>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-12 border-t border-[#E5E4DC]">
          <h3 className="font-heading-te text-2xl font-bold mb-6">
            <span className="text-[#2E7D32]">▍</span> ఇవి కూడా చదవండి
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {related.map((a) => <ArticleCard key={a.id} article={a} />)}
          </div>
        </section>
      )}
    </SiteLayout>
  );
}
